import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';

const SPECIALITY_OPTIONS = [
  'neck',
  'shoulder',
  'upper_back',
  'lower_back',
  'hip',
  'thigh',
  'knee',
  'ankle',
  'foot',
  'calf',
  'heel',
  'headache',
  'concussion',
];

const CARE_PROVIDER_TYPE_OPTIONS = [
  { value: '', label: 'Any clinic type' },
  { value: 'clinic', label: 'Clinic' },
  { value: 'physiotherapist', label: 'Physiotherapist' },
  { value: 'public_rehabilitation_center', label: 'Public rehabilitation center' },
  { value: 'private_care_provider', label: 'Private care provider' },
  { value: 'eldercare_provider', label: 'Eldercare provider' },
  { value: 'fitness_center', label: 'Fitness center' },
  { value: 'insurance_company', label: 'Insurance company' },
  { value: 'hospital', label: 'Hospital' },
];

const useStyles = makeStyles(() => ({
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  optionCard: {
    height: '100%',
    borderRadius: 18,
    boxShadow: '0 12px 28px rgba(0, 45, 85, 0.08)',
    border: '1px solid rgba(0, 45, 85, 0.10)',
  },
  optionCardActive: {
    border: '2px solid #fb741a',
    boxShadow: '0 18px 36px rgba(251, 116, 26, 0.18)',
  },
  introBox: {
    background: 'linear-gradient(180deg, rgba(220, 228, 235, 0.85) 0%, rgba(255, 255, 255, 0.95) 100%)',
    borderRadius: 18,
    padding: 20,
    marginBottom: 20,
  },
  filterBox: {
    background: '#ffffff',
    borderRadius: 18,
    padding: 20,
    boxShadow: '0 12px 28px rgba(0, 45, 85, 0.08)',
    marginBottom: 20,
  },
  resultCard: {
    height: '100%',
    borderRadius: 18,
    boxShadow: '0 14px 30px rgba(0, 45, 85, 0.10)',
  },
  chip: {
    marginRight: 8,
    marginBottom: 8,
  },
  primaryButton: {
    background: '#fb741a',
    color: '#fff',
    fontWeight: 600,
    '&:hover': {
      background: '#002D55',
    },
    borderRadius: 18,
  },
  secondaryButton: {
    border: '1px solid #002D55',
    color: '#002D55',
    background: 'transparent',
    fontWeight: 600,
    borderRadius: 18,
  },
}));

const formatSpecialityLabel = (value) => value.replace(/_/g, ' ');

const formatSlotDate = (value) => {
  if (!value) {
    return 'No slot available yet';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

function TemporaryClinicBookingDialog({ initialPath = 'assessment_first', open, onClose }) {
  const classes = useStyles();
  const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';
  const [selectedPath, setSelectedPath] = useState(initialPath);
  const [selectedSpecialities, setSelectedSpecialities] = useState(['neck']);
  const [careProviderType, setCareProviderType] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [addressQuery, setAddressQuery] = useState('');
  const [requiresInsurance, setRequiresInsurance] = useState(false);
  const [insuranceNetwork, setInsuranceNetwork] = useState('');
  const [requiresPublicSubsidy, setRequiresPublicSubsidy] = useState(false);
  const [availabilityWindow, setAvailabilityWindow] = useState('');
  const [availableFrom, setAvailableFrom] = useState('');
  const [availableTo, setAvailableTo] = useState('');
  const [insuranceCatalog, setInsuranceCatalog] = useState([]);
  const [patientName, setPatientName] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailFallbackUrl, setEmailFallbackUrl] = useState('');
  const [error, setError] = useState('');
  const [bookingUrl, setBookingUrl] = useState('');

  useEffect(() => {
    const handleBookingMessage = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type === 'virtus-public-booking-back-to-clinics') {
        setBookingUrl('');
      }
    };

    window.addEventListener('message', handleBookingMessage);
    return () => {
      window.removeEventListener('message', handleBookingMessage);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedPath(initialPath || 'assessment_first');
    setBookingUrl('');
    setError('');
    setEmailSuccess('');
    setEmailFallbackUrl('');
  }, [initialPath, open]);

  useEffect(() => {
    let cancelled = false;

    const fetchInsuranceCatalog = async () => {
      try {
        const response = await fetch(
          `${backendBaseUrl}/api/profile/public/insurance-support/catalog/?country=${encodeURIComponent(country || 'Denmark')}`,
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok || cancelled) {
          return;
        }
        setInsuranceCatalog(Array.isArray(data.insurance_support_catalog?.network_options) ? data.insurance_support_catalog.network_options : []);
      } catch {
        if (!cancelled) {
          setInsuranceCatalog([]);
        }
      }
    };

    fetchInsuranceCatalog();
    return () => {
      cancelled = true;
    };
  }, [backendBaseUrl, country]);

  const selectedSummary = useMemo(
    () => selectedSpecialities.map((speciality) => formatSpecialityLabel(speciality)).join(', '),
    [selectedSpecialities],
  );

  const toggleSpeciality = (speciality) => {
    setSelectedSpecialities((previous) => (
      previous.includes(speciality)
        ? previous.filter((item) => item !== speciality)
        : previous.concat(speciality)
    ));
  };

  const handleSelectPath = (path) => {
    setSelectedPath(path);
    setBookingUrl('');
    setError('');
    setEmailSuccess('');
    setEmailFallbackUrl('');
  };

  const handleDialogClose = () => {
    setBookingUrl('');
    setEmailFallbackUrl('');
    onClose();
  };

  const handleStartAssessmentFirst = () => {
    window.location.assign('/virtual_consultation?public_booking=true&temporary_test_mode=true');
  };

  const handleSearch = async () => {
    if (selectedSpecialities.length === 0) {
      setMatches([]);
      setError('Pick at least one speciality to search for a matching clinic.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        specialities: selectedSpecialities,
        temporary_test_mode: true,
      };

      if (city.trim()) {
        payload.city = city.trim();
      }
      if (country.trim()) {
        payload.country = country.trim();
      }
      if (careProviderType) {
        payload.care_provider_type = careProviderType;
      }
      if (addressQuery.trim()) {
        payload.address_query = addressQuery.trim();
      }
      if (requiresInsurance || insuranceNetwork) {
        payload.require_insurance_support = true;
      }
      if (insuranceNetwork) {
        payload.insurance_network = insuranceNetwork;
      }
      if (requiresPublicSubsidy) {
        payload.public_subsidy = true;
      }
      if (availabilityWindow) {
        payload.availability_window = availabilityWindow;
      }
      if (availabilityWindow === 'custom') {
        if (!availableFrom || !availableTo) {
          throw new Error('Choose both custom availability dates before searching clinics.');
        }
        payload.available_from = availableFrom;
        payload.available_to = availableTo;
      }

      const response = await fetch(`${backendBaseUrl}/api/profile/public/careproviders/match/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load matching clinics.');
      }

      setMatches(Array.isArray(data.care_providers) ? data.care_providers : []);
    } catch (matchError) {
      setMatches([]);
      setError(matchError.message || 'Failed to load matching clinics.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendAssessmentLink = async () => {
    if (!patientEmail.trim()) {
      setError('Enter an email address to send the assessment link.');
      setEmailSuccess('');
      setEmailFallbackUrl('');
      return;
    }

    setEmailSending(true);
    setError('');
    setEmailSuccess('');
    setEmailFallbackUrl('');

    try {
      const response = await fetch(`${backendBaseUrl}/user/public_assessment_link/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patient_email: patientEmail.trim(),
          patient_name: patientName.trim(),
          temporary_test_mode: true,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send assessment link.');
      }

      const fallbackUrl = data.full_assessment_url || data.assessment_url || '';
      if (data.email_sent === false && fallbackUrl) {
        setEmailFallbackUrl(fallbackUrl);
      }
      setEmailSuccess(data.message || `Assessment link sent to ${patientEmail.trim()}`);
    } catch (requestError) {
      setEmailFallbackUrl('');
      setError(requestError.message || 'Failed to send assessment link.');
    } finally {
      setEmailSending(false);
    }
  };

  const handleBookAndContinue = (clinic) => {
    const params = new URLSearchParams();
    const slot = clinic.earliest_available_slot;

    params.set('booking_first', 'true');
    params.set('temporary_test_mode', 'true');
    params.set('clinic_id', clinic.id);
    params.set('clinic_name', clinic.name_business || 'Selected clinic');
    params.set('city', clinic.city || '');
    params.set('country', clinic.country || '');
    params.set('specialities', selectedSpecialities.join(','));

    if (slot?.start_datetime) {
      params.set('slot_start', slot.start_datetime);
    }
    if (slot?.end_datetime) {
      params.set('slot_end', slot.end_datetime);
    }
    if (slot?.clinic_location_name) {
      params.set('location_name', slot.clinic_location_name);
    }
    if (slot?.clinic_location_id) {
      params.set('clinic_location_id', slot.clinic_location_id);
    }

    setBookingUrl(`/public/booking?${params.toString()}`);
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="lg" aria-labelledby="temporary-booking-dialog-title" PaperProps={{
    style: {
      borderRadius: 18,
      overflow: 'hidden'
    }
  }}>
      <DialogTitle disableTypography>
        <Box className={classes.titleRow}>
          <Box>
            <Typography id="temporary-booking-dialog-title" variant="h5" style={{ color: '#002D55', fontWeight: 700, }}>
              Better physiotherapy starts before the first appointment, and with the right match.
            </Typography>
            {/*<Typography variant="body2" style={{ color: '#486d86', marginTop: 4 }}>
              Patients can start with the assessment, start with clinic selection, or receive the public assessment link by email.
            </Typography>*/}
          </Box>
          <IconButton onClick={handleDialogClose} aria-label="Close dialog">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {bookingUrl ? (
          <Box>
            <Box className={classes.filterBox}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
                <Box>
                  <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                    Complete booking without leaving the landing page
                  </Typography>
                  <Typography variant="body2" style={{ color: '#486d86' }}>
                    The real public booking flow is running below inside the landing dialog. Close or go back here when you want to change clinic selection.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  className={classes.secondaryButton}
                  onClick={() => setBookingUrl('')}
                >
                  Back to clinic list
                </Button>
              </Box>
            </Box>
            <Box style={{ borderRadius: 18, overflow: 'hidden', boxShadow: '0 14px 30px rgba(0, 45, 85, 0.10)', background: '#fff' }}>
              <iframe
                title="Public booking"
                src={bookingUrl}
                style={{ width: '100%', minHeight: '78vh', border: 'none', display: 'block' }}
              />
            </Box>
          </Box>
        ) : (
          <>
        <Box className={classes.introBox}>
          <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>
            1.Choose how you want to start
          </Typography>
          {/*<Typography variant="body2" style={{ color: '#486d86' }}>
            This public entry point now mirrors the three patient starts you described. Assessment-first goes straight into the questionnaire, booking-first keeps the selected clinic and slot context, and email sends the patient a reusable public assessment-start link.
          </Typography>*/}
        </Box>

        <Grid container spacing={2} style={{ marginBottom: 20 }}>
          <Grid item xs={12} md={6}>
            <Card className={`${classes.optionCard} ${selectedPath === 'assessment_first' ? classes.optionCardActive : ''}`}>
              <CardContent>
                <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                  A. Use digital intake to find the right physiotherapist to book
                </Typography>
                <Typography variant="body2" style={{ color: '#486d86', marginBottom: 16 }}>
                  Start with symptoms and preferences, then choose a match and book. The digital intake information is shared with the clinic to help them prepare for the first appointment. Your physiotherapist will know you before you arrive. Freeing up their time to focus on you instead of asking routine questions.
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  className={classes.primaryButton}
                  onClick={handleStartAssessmentFirst}
                >
                 Find match with digital intake
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card className={`${classes.optionCard} ${selectedPath === 'booking_first' ? classes.optionCardActive : ''}`}>
              <CardContent>
                <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                  B. Find a clinic first, then complete the digital intake
                </Typography>
                <Typography variant="body2" style={{ color: '#486d86', marginBottom: 54 }}>
                  Select clinic preferences first, book appointment, then take the digital intake right after or later.
                </Typography>
                <Button
                  fullWidth
                  variant={selectedPath === 'booking_first' ? 'contained' : 'outlined'}
                  className={selectedPath === 'booking_first' ? classes.secondaryButton : classes.secondaryButton}
                  onClick={() => handleSelectPath('booking_first')}
                >
                  Find clinic first
                </Button>
              </CardContent>
            </Card>
          </Grid>
          {/*<Grid item xs={12} md={4}>
            <Card className={`${classes.optionCard} ${selectedPath === 'email_link' ? classes.optionCardActive : ''}`}>
              <CardContent>
                <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                  C. Assessment link by email
                </Typography>
                <Typography variant="body2" style={{ color: '#486d86', marginBottom: 16 }}>
                  Send the public assessment-start link to the patient so they can complete assessment and continue to booking later.
                </Typography>
                <Button
                  fullWidth
                  variant={selectedPath === 'email_link' ? 'contained' : 'outlined'}
                  className={selectedPath === 'email_link' ? classes.primaryButton : classes.secondaryButton}
                  onClick={() => handleSelectPath('email_link')}
                >
                  Send link by email
                </Button>
              </CardContent>
            </Card>
          </Grid>*/}
        </Grid>

        {selectedPath === 'booking_first' && (
        <Box className={classes.filterBox}>
          <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 14, textAlign: 'center' }}>
            2. Choose booking preferences
          </Typography>
          <Box style={{ marginBottom: 12 }}>
            {SPECIALITY_OPTIONS.map((speciality) => {
              const selected = selectedSpecialities.includes(speciality);
              return (
                <Chip
                  key={speciality}
                  label={formatSpecialityLabel(speciality)}
                  clickable
                  color={selected ? 'primary' : 'default'}
                  onClick={() => toggleSpeciality(speciality)}
                  className={classes.chip}
                />
              );
            })}
          </Box>
          <Typography variant="body2" style={{ color: '#486d86', marginBottom: 16 }}>
            Selected specialities: {selectedSummary || 'none yet'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="care-provider-type-label">Clinic type</InputLabel>
                <Select
                  labelId="care-provider-type-label"
                  value={careProviderType}
                  onChange={(event) => setCareProviderType(event.target.value)}
                  label="Clinic type"
                  style={{ borderRadius: 18 }}
                >
                  {CARE_PROVIDER_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'any'} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="City"
                variant="outlined"
                value={city}
                InputProps={{ style: { borderRadius: 18 } }}
                onChange={(event) => setCity(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Typed address or postcode"
                variant="outlined"
                value={addressQuery}
                InputProps={{ style: { borderRadius: 18 } }}
                onChange={(event) => setAddressQuery(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Country"
                variant="outlined"
                value={country}
                InputProps={{ style: { borderRadius: 18 } }}
                onChange={(event) => setCountry(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="temporary-availability-window-label">Availability</InputLabel>
                <Select
                  labelId="temporary-availability-window-label"
                  value={availabilityWindow}
                  onChange={(event) => setAvailabilityWindow(event.target.value)}
                  label="Availability"
                  style={{ borderRadius: 18 }}
                >
                  <MenuItem value="">Any upcoming time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="within_3_days">Within 3 days</MenuItem>
                  <MenuItem value="within_7_days">Within 7 days</MenuItem>
                  <MenuItem value="custom">Custom interval</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {availabilityWindow === 'custom' && (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Available from"
                    variant="outlined"
                    value={availableFrom}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ style: { borderRadius: 18 } }}
                    onChange={(event) => setAvailableFrom(event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Available to"
                    variant="outlined"
                    value={availableTo}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ style: { borderRadius: 18 } }}
                    onChange={(event) => setAvailableTo(event.target.value)}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                className={classes.primaryButton}
                onClick={handleSearch}
                disabled={loading}
                style={{ minHeight: 56 }}
              >
                Search Clinics
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={requiresInsurance}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setRequiresInsurance(checked);
                      if (!checked) {
                        setInsuranceNetwork('');
                      }
                    }}
                    style={{ color: requiresInsurance ? '#fb741a' : '#002D55' }}
                  />
                )}
                label="Require insurance support"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" disabled={!requiresInsurance && !insuranceNetwork}>
                <InputLabel id="temporary-insurance-network-label">Insurance network</InputLabel>
                <Select
                  labelId="temporary-insurance-network-label"
                  value={insuranceNetwork}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setInsuranceNetwork(nextValue);
                    if (nextValue) {
                      setRequiresInsurance(true);
                    }
                  }}
                  label="Insurance network"
                  style={{ borderRadius: 18 }}
                >
                  <MenuItem value="">Any supported insurer</MenuItem>
                  {insuranceCatalog.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={requiresPublicSubsidy}
                    onChange={(event) => setRequiresPublicSubsidy(event.target.checked)}
                    style={{ color: requiresPublicSubsidy ? '#fb741a' : '#002D55' }}
                  />
                )}
                label="Require public subsidy"
              />
            </Grid>
          </Grid>
        </Box>
        )}

        {selectedPath === 'email_link' && (
          <Box className={classes.filterBox}>
            <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 14 }}>
              1. Send the assessment link by email
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Patient name"
                  variant="outlined"
                  value={patientName}
                  onChange={(event) => setPatientName(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Patient email"
                  variant="outlined"
                  value={patientEmail}
                  onChange={(event) => setPatientEmail(event.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" style={{ color: '#486d86', marginBottom: 4 }}>
                  The email contains the public assessment-start link. After completion, the patient continues into clinician matching and booking.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  className={classes.primaryButton}
                  onClick={handleSendAssessmentLink}
                  disabled={emailSending}
                  style={{ minHeight: 56 }}
                >
                  {emailSending ? 'Sending...' : 'Send assessment link'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {selectedPath === 'booking_first' && loading && (
          <Box style={{ textAlign: 'center', padding: '24px 0' }}>
            <CircularProgress style={{ color: '#002D55' }} />
          </Box>
        )}

        {selectedPath === 'email_link' && emailSuccess && (
          <Box className={classes.filterBox}>
            <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
              Email status
            </Typography>
            <Typography variant="body2" style={{ color: '#486d86' }}>
              {emailSuccess}
            </Typography>
            {emailFallbackUrl && (
              <>
                <Typography variant="body2" style={{ color: '#486d86', marginTop: 12, marginBottom: 12 }}>
                  Local SMTP is unavailable, so use the generated assessment link below for testing.
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Assessment link"
                  value={emailFallbackUrl}
                  InputProps={{ readOnly: true }}
                />
                <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginTop: 12 }}>
                  <Button
                    variant="contained"
                    className={classes.primaryButton}
                    onClick={() => window.open(emailFallbackUrl, '_blank', 'noopener,noreferrer')}
                  >
                    Open assessment link
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}

        {!loading && !emailSending && error && (
          <Box className={classes.filterBox}>
            <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
              This step needs attention
            </Typography>
            <Typography variant="body2" style={{ color: '#486d86' }}>
              {error}
            </Typography>
          </Box>
        )}

        {selectedPath === 'booking_first' && !loading && matches.length > 0 && (
          <Box>
            <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 12, textAlign: 'center' }}>
              3. Pick a clinic and continue to the booking flow
            </Typography>
            <Grid container spacing={2}>
              {matches.map((clinic) => (
                <Grid item xs={12} md={6} key={clinic.id}>
                  <Card className={classes.resultCard}>
                    <CardContent>
                      <Typography variant="h6" style={{ color: '#002D55', fontWeight: 700, marginBottom: 6 }}>
                        {clinic.name_business}
                      </Typography>
                      <Typography variant="body2" style={{ color: '#486d86', marginBottom: 10 }}>
                        {clinic.city}, {clinic.country}
                      </Typography>
                      <Box style={{ marginBottom: 10 }}>
                        {(clinic.specialities || []).map((speciality) => (
                          <Chip
                            key={`${clinic.id}-${speciality}`}
                            label={formatSpecialityLabel(speciality)}
                            className={classes.chip}
                          />
                        ))}
                      </Box>
                      <Typography variant="subtitle2" style={{ color: '#002D55', fontWeight: 700 }}>
                        Earliest slot
                      </Typography>
                      <Typography variant="body2" style={{ color: '#486d86', marginBottom: 6 }}>
                        {formatSlotDate(clinic.earliest_available_slot?.start_datetime)}
                      </Typography>
                      <Typography variant="body2" style={{ color: '#486d86', marginBottom: 16 }}>
                        {clinic.earliest_available_slot?.clinic_location_name || 'Location to be confirmed'}
                      </Typography>
                      <Button
                        fullWidth
                        variant="contained"
                        className={classes.primaryButton}
                        onClick={() => handleBookAndContinue(clinic)}
                      >
                        Continue to booking
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {selectedPath === 'booking_first' && !loading && !error && matches.length === 0 && (
          <Box className={classes.filterBox}>
            <Typography variant="subtitle1" style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
              No clinics searched yet
            </Typography>
            <Typography variant="body2" style={{ color: '#486d86' }}>
              Choose your preferences and run a search to continue through the booking-first path.
            </Typography>
          </Box>
        )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default TemporaryClinicBookingDialog;